import { Alignment, AlignmentUtil, CurrencySettings, DOMUtil } from "@mcleod/core";
import {
    CloneComponent, Component, ComponentFactory, DropdownItem, Label, LabelProps, Panel, Textbox, TooltipOptions
} from "@mcleod/components";
import { CommonTooltips, KeyValuePair } from "@mcleod/common";
import { AutogenLayoutDemoTooltips } from "./autogen/AutogenLayoutDemoTooltips";
import { DemoDefinition } from "./DemoDefinition";
import { DemoRegistry } from "./DemoRegistry";

/*
Field Types:
 - Normal Textbox
 - Single-Select Type-Ahead Textbox
 - Multi-Select Type-Ahead Textbox
 - Single-Select Dropdown Textbox
 - Multi-Select Dropdown Textbox
 - Multi-Currency Textbox
 - Button

Tooltip Sources:
 - Tooltip
 - Validation Warning
 - Disabled Tooltip
 - Dynamic Content (static)
 - Dynamic Content (async)
 - Quick Info Content
 */

export class DemoTooltips extends AutogenLayoutDemoTooltips {
    private mapClonedComponents: Map<string, Component[]> = new Map();
    private panelHeaderExpanded = true;

    override onLoad() {
        this.panelBulletedLabelContainer.add(...this.buildBulletedLabels());
        this.cloneMasterComponents();
        this.configureTypeAheadComponents();
        this.removeMultiCurrencyFieldType();
        this.setInitialDropdownValues();
        this.makeScrollable();
    }

    private buildBulletedLabels(): Label[] {
        const labels = [
            "Specify any desired tooltip options below",
            "Hover over any of the buttons in the lower section to display the tooltip for that button",
            "Click a button to change the positioning of that button's tooltip"
        ].map(labelText => this.buildBulletedLabelProps(labelText));
        return ComponentFactory.createBulletedList(labels);
    }

    private buildBulletedLabelProps(text: string): Partial<LabelProps> {
        return { text, marginLeft: 16 };
    }

    private cloneMasterComponents() {
        const fieldTypes = (this.textboxFieldType.items as DropdownItem[]).map(item => item.value);
        this.components
        .filter(component => {
            return fieldTypes.includes(component.id);
        })
        .forEach(component => {
            const cloneArray: Component[] = [];
            for (let x=1 ; x<10 ; x++) {
                const clonedComponent = CloneComponent.clone({
                    component,
                    idCalcFunction: sourceComponent => `${sourceComponent.id}-${x}`,
                    overrideProps: { visible: true, rowBreak: (x%3) === 0 }
                });
                clonedComponent.tooltipDynamicOptions = () => this.getTooltipOptions();
                if (component === this.textbox_multiCurrency) {
                    clonedComponent.doWhileOffScreen(() => {
                        const currencyTextbox = clonedComponent as Textbox;
                        currencyTextbox.text = "500";
                        currencyTextbox.focus();
                        currencyTextbox["getFocusTarget"]().blur();
                    });
                }
                cloneArray.push(clonedComponent);
            }
            this.mapClonedComponents.set(component.id, cloneArray);
        });
    }

    private removeMultiCurrencyFieldType() {
        if (CurrencySettings.getSingleton()?.multiCurrencyEnabled() !== true) {
            this.textboxFieldType.removeItemsWithValue("textbox_multiCurrency");
        }
    }

    private configureTypeAheadComponents() {
        const fn = (component: Component) => {
            const textbox = component as Textbox;
            textbox.quickInfoOverrideKeyValue = () => {
                return this.checkboxQuickInfoSource.checked ?
                textbox["getLookupModelResultValue"]() :
                null;
            };
        }
        const filterFn = (component: Component) => "lookupModel" in component && component["lookupModel"] != null;
        this.forAllClonedComponents(fn, filterFn);
    }

    private setInitialDropdownValues() {
        this.textboxPosition.setSelectedItemFromValue(Alignment.BOTTOM);
        this.textboxFieldType.setSelectedItemFromValue("textbox_normal");
    }

    // Make the demo panel scroll vertically, so that we can test the 'dismiss tooltips on scroll' logic.
    // But keep the bottom row of buttons visible to the user
    private makeScrollable() {
        const heightAdjustment = 100;
        this.addMountListener(() => this.height = DOMUtil.getElementHeight(this._element) + heightAdjustment);
        this.panelLowerRow3.marginBottom = heightAdjustment;
    }

    buttonChevronOnClick() {
        if (this.panelHeaderExpanded) {
            this.collapsePanelHeader();
        } else {
            this.expandPanelHeader();
        }
    }

    public collapsePanelHeader() {
        this.panelHeaderExpanded = false;
        this.setChevronImageRotation();
        this.panelHeader.collapse({ speed: 400 });
    }

    public async expandPanelHeader() {
        this.panelHeaderExpanded = true;
        this.setChevronImageRotation();
        this.panelHeader.expand({ speed: 400 });
    }

    private setChevronImageRotation() {
        this.buttonChevron.imageRotation = this.panelHeaderExpanded ? 0 : 90;
    }

    textboxFieldTypeAfterDropdownSelection() {
        this.syncPrintableSwitch();
        this.syncValidationWarningCheckbox();
        this.syncQuickInfoCheckbox();
        this.syncAllowNestedTooltipsCheckbox();
        this.displayClonedComponents();
    }

    switchPrintableOnChange() {
        const fn = (component: Component) => component["printable"] = this.switchPrintable.checked;
        const filterFn = (component: Component) => "printable" in component;
        this.forAllClonedComponents(fn, filterFn);
    }

    private get currentMasterComponent(): Component {
        return this[this.textboxFieldType.selectedItem.value] as Component;
    }

    private syncPrintableSwitch() {
        if (this.currentMasterComponent.serializationName === "textbox") {
            this.switchPrintable.enabled = true;
        } else {
            this.switchPrintable.enabled = false;
            this.switchPrintable.checked = false;
        }
    }

    private syncValidationWarningCheckbox() {
        if (this.currentMasterComponent.serializationName === "textbox") {
            this.checkboxValidationWarningSource.enabled = true;
        } else {
            this.checkboxValidationWarningSource.enabled = false;
            this.checkboxValidationWarningSource.checked = false;
        }
    }

    private syncQuickInfoCheckbox() {
        let enabled = false;
        let checked = false;
        if (this.currentMasterComponent.serializationName === "textbox") {
            const textbox = this.currentMasterComponent as Textbox;
            if (textbox !== this.textbox_multiCurrency && textbox.hasLookupModel()) {
                enabled = textbox.lookupModelAllowMultiSelect !== true;
                checked = true;
            }
        }
        this.checkboxQuickInfoSource.enabled = enabled;
        this.checkboxQuickInfoSource.checked = checked;
    }

    private syncAllowNestedTooltipsCheckbox() {
        if (this.currentMasterComponent !== this.textbox_multiCurrency) {
            this.checkboxAllowNested.enabled = true;
        } else {
            this.checkboxAllowNested.enabled = false;
            this.checkboxAllowNested.checked = false;
        }
    }

    private displayClonedComponents() {
        const clonedArray = this.mapClonedComponents.get(this.textboxFieldType.selectedItem.value);
        this.panelLowerRow1.removeAll();
        this.panelLowerRow1.add(
            clonedArray[0],
            this.horizontalspacerRow1Left,
            clonedArray[1],
            this.horizontalspacerRow1Right,
            clonedArray[2],
        );
        this.panelLowerRow2.removeAll();
        this.panelLowerRow2.add(
            clonedArray[3],
            this.horizontalspacerRow2Left,
            clonedArray[4],
            this.horizontalspacerRow2Right,
            clonedArray[5],
        );
        this.panelLowerRow3.removeAll();
        this.panelLowerRow3.add(
            clonedArray[6],
            this.horizontalspacerRow3Left,
            clonedArray[7],
            this.horizontalspacerRow3Right,
            clonedArray[8],
        );
    }

    private getTooltipOptions: () => TooltipOptions = () => {
        const result: TooltipOptions = {};
        result.position = AlignmentUtil.fromString(this.textboxPosition.selectedItem?.value) ?? Alignment.BOTTOM;
        result.shaking = this.checkboxShakeTooltip.checked;
        result.allowNestedTooltips = this.checkboxAllowNested.checked;
        return result;
    };

    private forAllClonedComponents(fn: (component: Component) => void, filterFn?: (component: Component) => boolean) {
        for (const clonedArray of this.mapClonedComponents.values()) {
            clonedArray.forEach(component => {
                if (filterFn == null || filterFn(component)) {
                    fn(component);
                }
            });
        }
    }

    checkboxTooltipSourceOnChange() {
        const includingTooltip = this.checkboxTooltipSource.checked;
        const masterComponentTooltip = this.currentMasterComponent.tooltip;
        const clonedComponentTooltip = includingTooltip ? masterComponentTooltip : null;
        const fn = (component: Component) => component.tooltip = clonedComponentTooltip;
        this.forAllClonedComponents(fn);
    }

    checkboxValidationWarningSourceOnChange() {
        const validationWarning = this.checkboxValidationWarningSource.checked ?
            "This text would indicate the nature of the validation warning" :
            null;
        const fn = (component: Component) => component["validationWarning"] = validationWarning;
        const filterFn = (component: Component) => "validationWarning" in component;
        this.forAllClonedComponents(fn, filterFn);
    }
    
    checkboxDisabledTooltipSourceOnChange() {
        const value = !this.checkboxDisabledTooltipSource.checked;
        const fn = (component: Component) => component.enabled = value;
        this.forAllClonedComponents(fn);
    }

    checkboxDynamicSourceSyncOnChange() {
        if (this.checkboxDynamicSourceSync.checked) {
            this.checkboxDynamicSourceAsync.checked = false;
        }
        const fn = (component: Component) => {
            component.tooltipDynamicContent = this.checkboxDynamicSourceSync.checked ?
                this.getTooltipDynamicContentSync :
                null;
        };
        this.forAllClonedComponents(fn);
    }

    private getTooltipDynamicContentSync: (component: Component) => Component = (component: Component) => {
        return this.createSharedDynamicContent(component);
    };

    checkboxDynamicSourceAsyncOnChange() {
        if (this.checkboxDynamicSourceAsync.checked) {
            this.checkboxDynamicSourceSync.checked = false;
        }
        const fn = (component: Component) => {
            component.tooltipDynamicContent = this.checkboxDynamicSourceAsync.checked ?
                this.getTooltipDynamicContentAsync :
                null;
        };
        this.forAllClonedComponents(fn);
    }

    private getTooltipDynamicContentAsync: (component: Component) => Promise<Component> = async (component: Component) => {
        await new Promise<void>(resolve => {
            setTimeout(() => {
                resolve();
            }, 1500); // 1.5 second delay
        });
        return this.createSharedDynamicContent(component);
    };

    private createSharedDynamicContent(component: Component): Panel {
        const keyValuePairs: KeyValuePair[] = [
            new KeyValuePair("Key 1", "Value 1"),
            new KeyValuePair("Key 2", "Value 2")
        ];
        const headerLabel = "This is dynamic content that was produced asynchronously (including a delay).";
        const footerLabel: Partial<LabelProps> = this.checkboxAllowNested ?
            new NestedTooltipLabel(component, {
                text: this.checkboxAllowNested.checked ?
                    "Hover here to display a nested tooltip" :
                    "Hovering here will not display a nested tooltip, as they are not allowed",
                marginTop: 10
            }) :
            null;
        return CommonTooltips.createKeyValueTooltipContent(keyValuePairs, headerLabel, footerLabel);
    }
}

class NestedTooltipLabel extends Label {
    private parentComponent: Component;

    constructor(parentComponent: Component, props: Partial<LabelProps>) {
        super(props);
        this.parentComponent = parentComponent;
        this.tooltipDynamicOptions = this.parentComponent.tooltipDynamicOptions;
        this.syncTooltipMouseListeners();
    }

    override get tooltipSources() {
        return this.parentComponent.tooltipSources;
    }

    override get tooltipPosition(): Alignment {
        return this.parentComponent.tooltipPosition;
    }
}
DemoRegistry.get().register(new DemoDefinition("DemoTooltips", [ "Concepts", "Tooltips" ]));
